import React, { useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import SelfAssessment from './SelfAssessment';

/**
 * SelfAssessmentTask — the self-assessment's container (#693): asks the
 * backend which items this wave has and whether this person has answered,
 * holds the draft, posts once. The statements themselves are locale strings
 * keyed by item id, so the backend stores ids and numbers only.
 */
export default function SelfAssessmentTask({ instanceId, wave = 'pre', onChange = null }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const q = `?instanceId=${encodeURIComponent(instanceId)}&wave=${encodeURIComponent(wave)}`;

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.ME_SELF_ASSESSMENT() + q, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; })
      .then((d) => { if (!cancelled) setState({ loading: false, error: null, data: d }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message, data: null }); });
    return () => { cancelled = true; };
  }, [instanceId, wave]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.ME_SELF_ASSESSMENT(), {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ instanceId, wave, answers }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setState({ loading: false, error: null, data: d });
      onChange?.(d);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (state.loading) return <p style={{ color: '#9aa4b2' }}>{t('me.loading')}</p>;
  if (state.error) return <p style={{ color: '#FF8A85' }}>{state.error}</p>;
  const d = state.data;
  const items = (d.items || []).map((id) => ({ id, text: t(`me.selfAssessment.items.${id}`) }));
  return (
    <SelfAssessment wave={wave} items={items} answers={answers} onChange={setAnswers} onSubmit={submit}
      submitted={!!d.submitted} submittedAt={d.submittedAt || null} own={d.answers || null} busy={busy} error={error} />
  );
}
